import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, Chip,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
} from '@mui/material';
import {
  Add, Edit, Delete, Laptop, PhoneAndroid, AssignmentReturn,
  CheckCircle, Inventory2, PersonAdd,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { displayName } from '../../types';

const CATEGORIES = ['laptop', 'phone', 'tablet', 'monitor', 'keyboard', 'access_card', 'vehicle', 'other'];
const STATUSES = ['available', 'assigned', 'under_repair', 'retired', 'lost'];
const CONDITIONS = ['Excellent', 'Good', 'Fair', 'Damaged'];

const categoryLabel = (c: string) => c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const statusColor: Record<string, any> = {
  available: 'success',
  assigned: 'info',
  under_repair: 'warning',
  retired: 'default',
  lost: 'error',
};

const categoryIcon = (cat: string) => {
  if (cat === 'laptop' || cat === 'tablet') return <Laptop fontSize="small" />;
  if (cat === 'phone') return <PhoneAndroid fontSize="small" />;
  return <Inventory2 fontSize="small" />;
};

const Assets: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [assets, setAssets] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Asset dialog
  const [assetDialog, setAssetDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState<any>(null);
  const [assetForm, setAssetForm] = useState({
    name: '', category: 'laptop', brand: '', model: '',
    serialNumber: '', assetTag: '', status: 'available',
    purchaseDate: '', purchasePrice: '', warrantyExpiry: '', notes: '',
  });

  // Assign dialog
  const [assignDialog, setAssignDialog] = useState<any>(null); // the asset being assigned
  const [assignForm, setAssignForm] = useState({
    employeeId: '', assignedDate: new Date().toISOString().split('T')[0],
    condition: 'Good', notes: '',
  });

  // Return dialog
  const [returnDialog, setReturnDialog] = useState<any>(null); // { asset, assignment }
  const [returnForm, setReturnForm] = useState({
    returnedDate: new Date().toISOString().split('T')[0], condition: 'Good', notes: '',
  });

  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [a, asn, s, emps] = await Promise.all([
        apiService.getAssets().catch(() => []),
        apiService.getAllAssetAssignments().catch(() => []),
        apiService.getAssetStats().catch(() => null),
        apiService.getEmployees().catch(() => []),
      ]);
      setAssets(a);
      setAssignments(asn);
      setStats(s);
      setEmployees(emps);
    } catch {
      setError('Failed to load asset data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openAssetDialog = (asset?: any) => {
    if (asset) {
      setEditingAsset(asset);
      setAssetForm({
        name: asset.name, category: asset.category, brand: asset.brand || '',
        model: asset.model || '', serialNumber: asset.serialNumber || '',
        assetTag: asset.assetTag || '', status: asset.status,
        purchaseDate: asset.purchaseDate || '', purchasePrice: asset.purchasePrice ?? '',
        warrantyExpiry: asset.warrantyExpiry || '', notes: asset.notes || '',
      });
    } else {
      setEditingAsset(null);
      setAssetForm({ name: '', category: 'laptop', brand: '', model: '', serialNumber: '', assetTag: '', status: 'available', purchaseDate: '', purchasePrice: '', warrantyExpiry: '', notes: '' });
    }
    setAssetDialog(true);
  };

  const saveAsset = async () => {
    if (!assetForm.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...assetForm,
        purchasePrice: assetForm.purchasePrice ? Number(assetForm.purchasePrice) : undefined,
        purchaseDate: assetForm.purchaseDate || undefined,
        warrantyExpiry: assetForm.warrantyExpiry || undefined,
      };
      if (editingAsset) {
        await apiService.updateAsset(editingAsset.id, payload);
        setSuccess('Asset updated');
      } else {
        await apiService.createAsset(payload);
        setSuccess('Asset added');
      }
      setAssetDialog(false);
      fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  const deleteAsset = async (id: string) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      await apiService.deleteAsset(id);
      setSuccess('Asset deleted');
      fetchData();
    } catch {
      setError('Failed to delete asset');
    }
  };

  const handleAssign = async () => {
    if (!assignForm.employeeId || !assignDialog) return;
    setSaving(true);
    try {
      await apiService.assignAsset(assignDialog.id, assignForm);
      setSuccess(`${assignDialog.name} assigned successfully`);
      setAssignDialog(null);
      fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to assign asset');
    } finally {
      setSaving(false);
    }
  };

  const handleReturn = async () => {
    if (!returnDialog) return;
    setSaving(true);
    try {
      await apiService.returnAsset(returnDialog.asset.id, returnDialog.assignment.id, returnForm);
      setSuccess(`${returnDialog.asset.name} returned successfully`);
      setReturnDialog(null);
      fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to process return');
    } finally {
      setSaving(false);
    }
  };

  const openReturnDialog = (asset: any) => {
    const activeAssignment = asset.assignments?.find((a: any) => !a.isReturned);
    if (!activeAssignment) return;
    setReturnDialog({ asset, assignment: activeAssignment });
    setReturnForm({ returnedDate: new Date().toISOString().split('T')[0], condition: 'Good', notes: '' });
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="400px">
      <CircularProgress />
    </Box>
  );

  const activeAssignments = assignments.filter(a => !a.isReturned);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>Asset Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Track company equipment assigned to employees
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => openAssetDialog()}
          sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#A3501A' } }}>
          Add Asset
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: stats?.total ?? 0, color: 'text.primary' },
          { label: 'Available', value: stats?.available ?? 0, color: 'success.main' },
          { label: 'Assigned', value: stats?.assigned ?? 0, color: 'info.main' },
          { label: 'Under Repair', value: stats?.underRepair ?? 0, color: 'warning.main' },
        ].map(s => (
          <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">{s.label}</Typography>
                <Typography variant="h4" color={s.color}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={`All Assets (${assets.length})`} />
          <Tab label={`Currently Assigned (${activeAssignments.length})`} />
          <Tab label={`Assignment History (${assignments.length})`} />
        </Tabs>
      </Paper>

      {/* All Assets */}
      {tab === 0 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Serial / Tag</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Purchase Date</TableCell>
                  <TableCell>Warranty</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No assets yet — add one to get started
                    </TableCell>
                  </TableRow>
                ) : assets.map(asset => {
                  const activeAssignment = asset.assignments?.find((a: any) => !a.isReturned);
                  const warrantyExpired = asset.warrantyExpiry && new Date(asset.warrantyExpiry) < new Date();
                  return (
                    <TableRow key={asset.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {categoryIcon(asset.category)}
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{asset.name}</Typography>
                            {(asset.brand || asset.model) && (
                              <Typography variant="caption" color="text.secondary">
                                {[asset.brand, asset.model].filter(Boolean).join(' ')}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={categoryLabel(asset.category)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{asset.serialNumber || asset.assetTag || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={categoryLabel(asset.status)} color={statusColor[asset.status]} size="small" />
                      </TableCell>
                      <TableCell>
                        {activeAssignment ? (
                          <Typography variant="body2">
                            {activeAssignment.employee ? displayName(activeAssignment.employee) : '—'}
                          </Typography>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </TableCell>
                      <TableCell>
                        {asset.warrantyExpiry ? (
                          <Typography variant="body2" color={warrantyExpired ? 'error' : 'text.primary'}>
                            {new Date(asset.warrantyExpiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {warrantyExpired && ' (expired)'}
                          </Typography>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {asset.status === 'available' && (
                          <Tooltip title="Assign to employee">
                            <IconButton size="small" color="primary"
                              onClick={() => { setAssignDialog(asset); setAssignForm({ employeeId: '', assignedDate: new Date().toISOString().split('T')[0], condition: 'Good', notes: '' }); }}>
                              <PersonAdd fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {asset.status === 'assigned' && (
                          <Tooltip title="Process return">
                            <IconButton size="small" color="warning" onClick={() => openReturnDialog(asset)}>
                              <AssignmentReturn fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openAssetDialog(asset)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => deleteAsset(asset.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Currently Assigned */}
      {tab === 1 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Serial / Tag</TableCell>
                  <TableCell>Assigned Date</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No assets currently assigned
                    </TableCell>
                  </TableRow>
                ) : activeAssignments.map(a => (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {a.employee ? displayName(a.employee) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{a.employee?.department?.name || '—'}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {a.asset && categoryIcon(a.asset.category)}
                        <Typography variant="body2">{a.asset?.name || '—'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {a.asset && <Chip label={categoryLabel(a.asset.category)} size="small" variant="outlined" />}
                    </TableCell>
                    <TableCell><Typography variant="caption">{a.asset?.serialNumber || a.asset?.assetTag || '—'}</Typography></TableCell>
                    <TableCell>{a.assignedDate ? new Date(a.assignedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</TableCell>
                    <TableCell>{a.condition || '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Process return">
                        <IconButton size="small" color="warning"
                          onClick={() => { setReturnDialog({ asset: a.asset, assignment: a }); setReturnForm({ returnedDate: new Date().toISOString().split('T')[0], condition: 'Good', notes: '' }); }}>
                          <AssignmentReturn fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Assignment History */}
      {tab === 2 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell>Returned</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map(a => (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.employee ? displayName(a.employee) : '—'}</TableCell>
                    <TableCell>{a.asset?.name || '—'}</TableCell>
                    <TableCell>{a.assignedDate ? new Date(a.assignedDate).toLocaleDateString('en-GB') : '—'}</TableCell>
                    <TableCell>{a.returnedDate ? new Date(a.returnedDate).toLocaleDateString('en-GB') : '—'}</TableCell>
                    <TableCell>{a.condition || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={a.isReturned ? 'Returned' : 'Active'}
                        color={a.isReturned ? 'default' : 'info'}
                        size="small"
                        icon={a.isReturned ? <CheckCircle /> : undefined}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add / Edit Asset Dialog */}
      <Dialog open={assetDialog} onClose={() => setAssetDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={8}>
              <TextField fullWidth label="Asset Name" value={assetForm.name} required
                onChange={e => setAssetForm({ ...assetForm, name: e.target.value })} />
            </Grid>
            <Grid size={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={assetForm.category} onChange={e => setAssetForm({ ...assetForm, category: e.target.value })} label="Category">
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{categoryLabel(c)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Brand" value={assetForm.brand}
                onChange={e => setAssetForm({ ...assetForm, brand: e.target.value })} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Model" value={assetForm.model}
                onChange={e => setAssetForm({ ...assetForm, model: e.target.value })} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Serial Number" value={assetForm.serialNumber}
                onChange={e => setAssetForm({ ...assetForm, serialNumber: e.target.value })} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Asset Tag" value={assetForm.assetTag}
                onChange={e => setAssetForm({ ...assetForm, assetTag: e.target.value })} />
            </Grid>
            <Grid size={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={assetForm.status} onChange={e => setAssetForm({ ...assetForm, status: e.target.value })} label="Status">
                  {STATUSES.map(s => <MenuItem key={s} value={s}>{categoryLabel(s)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={4}>
              <TextField fullWidth label="Purchase Date" type="date" value={assetForm.purchaseDate}
                onChange={e => setAssetForm({ ...assetForm, purchaseDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={4}>
              <TextField fullWidth label="Purchase Price" type="number" value={assetForm.purchasePrice}
                onChange={e => setAssetForm({ ...assetForm, purchasePrice: e.target.value })} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Warranty Expiry" type="date" value={assetForm.warrantyExpiry}
                onChange={e => setAssetForm({ ...assetForm, warrantyExpiry: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Notes" multiline rows={2} value={assetForm.notes}
                onChange={e => setAssetForm({ ...assetForm, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssetDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAsset} disabled={saving || !assetForm.name.trim()}
            sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#A3501A' } }}>
            {saving ? <CircularProgress size={20} /> : editingAsset ? 'Save Changes' : 'Add Asset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={!!assignDialog} onClose={() => setAssignDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign — {assignDialog?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Assign To</InputLabel>
                <Select value={assignForm.employeeId} onChange={e => setAssignForm({ ...assignForm, employeeId: e.target.value })} label="Assign To">
                  {employees.map((emp: any) => <MenuItem key={emp.id} value={emp.id}>{displayName(emp)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Date" type="date" value={assignForm.assignedDate}
                onChange={e => setAssignForm({ ...assignForm, assignedDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Condition</InputLabel>
                <Select value={assignForm.condition} onChange={e => setAssignForm({ ...assignForm, condition: e.target.value })} label="Condition">
                  {CONDITIONS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Notes" value={assignForm.notes}
                onChange={e => setAssignForm({ ...assignForm, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={saving || !assignForm.employeeId}>
            {saving ? <CircularProgress size={20} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={!!returnDialog} onClose={() => setReturnDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Return — {returnDialog?.asset?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={6}>
              <TextField fullWidth label="Return Date" type="date" value={returnForm.returnedDate}
                onChange={e => setReturnForm({ ...returnForm, returnedDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Condition on Return</InputLabel>
                <Select value={returnForm.condition} onChange={e => setReturnForm({ ...returnForm, condition: e.target.value })} label="Condition on Return">
                  {CONDITIONS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Notes" value={returnForm.notes}
                onChange={e => setReturnForm({ ...returnForm, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleReturn} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Confirm Return'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Assets;
